import {
  directionArrowRight,
  imgElementSvg,
} from '../../../components/shared/vector-images';
import { telemetry } from '../../backend';
import { getLocationsLineString } from '../../log-trips/background-geolocation';
import { definitions, subscribe } from '../../message-broker';
import captureSpecificError from '../../sentry';
import { getStore } from '../../store';
import {
  layerExists,
  setDataSource,
  sourceExists,
  toggleLayerIfRequired,
} from '../_helpers';

let mapReference;

const loadDirectionArrow = async () => {
  const arrow = await imgElementSvg(directionArrowRight(), {
    width: 90,
    height: 90,
    fillColor: '#FF5C39',
  });
  if (!mapReference.hasImage('logTripsDirectionArrow'))
    mapReference.addImage('logTripsDirectionArrow', arrow);
};

const getFeatureCollection = async () => {
  const { data } = await getLocationsLineString();

  const geojson = {
    type: 'FeatureCollection',
    features: [data],
  };
  telemetry(
    telemetry.DEBUG,
    `[logTrips mapLayer] Received data: ${JSON.stringify(geojson)}`,
  );
  return geojson;
};

const addLayersIfRequired = () => {
  if (!sourceExists(mapReference, 'logTrips')) {
    mapReference.addSource('logTrips', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
  }
  if (!layerExists(mapReference, 'logTrips')) {
    mapReference.addLayer({
      id: 'logTrips',
      type: 'line',
      source: 'logTrips',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#FF5C39',
        'line-width': 7,
      },
    });
    mapReference.addLayer({
      id: 'logTripsArrowLayer',
      type: 'symbol',
      source: 'logTrips',
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 100,
        'icon-allow-overlap': true,
        'icon-image': 'logTripsDirectionArrow',
        'icon-size': 0.33,
        visibility: 'visible',
      },
    });
  }
};

const layer = {
  displayName: 'Log Trips',
  synonyms: '',
  group: 'tileLayer',
  typeUrl: 'log-trips',
  shownByDefault: true,
  relativeDepth: 10,
  menuOptions: {
    visible: true,
    owningMenu: 'log-trips',
    order: 0,
  },
  icon: null,
};

export const removeActiveTripFromMap = () => {
  setDataSource(mapReference, 'logTrips', {
    type: 'FeatureCollection',
    features: [],
  });
};

// NOTES: THIS IS STILL A WIP
//  - need to unify with loadRemoteTripDetails in loaded-trip.js
//  - test use of it here in onInitialize
//  - what other functions in here need this check added if it cant be done on initialise
//  - should we extract to a common place and if so where?
export const hideActiveTripFromMap = (hide) => {
  // Sometimes the map hasn't loaded by the time we want to show the trip
  // This checks for the mapReference repeatedly until it's there
  if (mapReference) {
    mapReference.setLayoutProperty(
      'logTrips',
      'visibility',
      hide ? 'none' : 'visible',
    );
    mapReference.setLayoutProperty(
      'logTripsArrowLayer',
      'visibility',
      hide ? 'none' : 'visible',
    );
  } else {
    setTimeout(() => hideActiveTripFromMap(hide), 500);
  }
};

const loadData = async () => {
  try {
    if (!getStore().getState()?.logTrips?.activeTripHidden) {
      hideActiveTripFromMap(false);
      const geojson = await getFeatureCollection();
      setDataSource(mapReference, 'logTrips', geojson);
    } else {
      hideActiveTripFromMap(true);
    }
  } catch (error) {
    telemetry(
      telemetry.WARN,
      `[logTrips mapLayer] Failed to load any trips, ${error}`,
    );
  }
};

layer.onInitialize = (map) => {
  mapReference = map;
  addLayersIfRequired();

  loadDirectionArrow();

  loadData();
};

const updateData = async (currentUserLocation) => {
  try {
    // If we have yet to receive a reference to the map object through the onInitialize function,
    // just return as we can't update the data on the layer if the layer doesn't exist yet.
    if (!mapReference) return;
    addLayersIfRequired();

    const { followModeActive, tripPaused, activeTrip } =
      getStore().getState().logTrips;

    // NOTE: WIP still testing if this is needed
    //  - pretty sure this is now fixed with prop update fixes
    // if (!activeTrip) {
    //   removeActiveTripFromMap();
    //   return;
    // }

    const geojson = await getFeatureCollection();
    // Prepend current user location so trip on front end doesn't lag behind
    try {
      if (
        followModeActive &&
        !tripPaused &&
        activeTrip &&
        currentUserLocation
      ) {
        geojson.features[0].geometry.coordinates.push([
          currentUserLocation.lon,
          currentUserLocation.lat,
        ]);
      }
    } catch (error) {
      // Ignore if fails to prepend
      captureSpecificError(
        error,
        'containers/app-main-client/src/services/map-layer/line-laters/log-trips.js',
        { deckeeId: '[updateData] try to push coordinates' },
      );
    }

    setDataSource(mapReference, 'logTrips', geojson);
  } catch (error) {
    // Something happened here, this is still in debugging
    captureSpecificError(
      error,
      'containers/app-main-client/src/services/map-layer/line-laters/log-trips.js',
      { deckeeId: '[updateData] general error' },
    );
  }
};

layer.onToggle = (map, show) => {
  toggleLayerIfRequired(map, 'logTrips', show);
};

subscribe(definitions.map.userLocationUpdated, (userLocation) => {
  updateData(userLocation);
});

subscribe(definitions.map.logTripStatusChange, () => {
  updateData();
});

export default layer;
